import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../config/axios";
import {
  compareBaseTradeNumberDesc,
  progressReportTradeLabel,
} from "../utils/progressReportUtils";
import {
  filterCommissionTrustActivityEFTs,
  formatCurrency,
  formatDate,
  getEFTTypeDisplayName,
} from "../utils/eftUtils";

const tradeSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "0.875rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 80 }),
  menuPortal: (base) => ({ ...base, zIndex: 80 }),
};

function EftTable({ title, rows, emptyMessage }) {
  if (!rows.length) {
    return (
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-xs text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-2 py-2 text-left font-semibold border-b">EFT #</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Date</th>
              <th className="px-2 py-2 text-right font-semibold border-b">Amount</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Recipient</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Type</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((eft) => (
              <tr key={eft._id || `${eft.eftNumber}-${eft.date}`} className="border-b border-gray-100">
                <td className="px-2 py-1.5 whitespace-nowrap">{eft.eftNumber ?? "—"}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {formatDate(eft.chequeDate || eft.date)}
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {formatCurrency(eft.amount)}
                </td>
                <td className="px-2 py-1.5">{eft.recipient || "—"}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {getEFTTypeDisplayName(eft.type) || "—"}
                </td>
                <td className="px-2 py-1.5 min-w-[12rem]">{eft.description || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReversalsTable({ rows }) {
  if (!rows.length) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">EFT Reversals</h3>
        <p className="text-xs text-gray-500">No EFT reversals for this trade.</p>
      </div>
    );
  }

  const sectionLabel = (section) => {
    if (section === "real-estate") return "Real Estate Trust";
    if (section === "commission") return "Commission Trust";
    if (section === "general") return "General Account";
    if (section === "deposit-received") return "Commission Received";
    return section || "—";
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-2">EFT Reversals</h3>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-2 py-2 text-left font-semibold border-b">Date</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Section</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Original EFT #</th>
              <th className="px-2 py-2 text-right font-semibold border-b">Amount</th>
              <th className="px-2 py-2 text-left font-semibold border-b">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="border-b border-gray-100">
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {formatDate(r.reversedAt || r.chequeDate)}
                </td>
                <td className="px-2 py-1.5">{sectionLabel(r.section)}</td>
                <td className="px-2 py-1.5">{r.sourceEftNumber || "—"}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {formatCurrency(r.amount)}
                </td>
                <td className="px-2 py-1.5">{r.description || r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CommissionDetailsModal = ({ isOpen, onClose, trades, loadingTrades }) => {
  const [selectedTradeId, setSelectedTradeId] = useState("");
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [realEstateTrustEFTs, setRealEstateTrustEFTs] = useState([]);
  const [commissionTrustEFTs, setCommissionTrustEFTs] = useState([]);
  const [eftReversals, setEftReversals] = useState([]);

  const tradeOptions = useMemo(
    () =>
      (trades || [])
        .map((t) => ({
          value: String(t._id),
          label: progressReportTradeLabel(t),
          sortKey: String(t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "").trim(),
        }))
        .sort((a, b) => compareBaseTradeNumberDesc(a.sortKey, b.sortKey)),
    [trades],
  );

  const selectedTradeOption = useMemo(
    () => tradeOptions.find((o) => o.value === selectedTradeId) || null,
    [tradeOptions, selectedTradeId],
  );

  const selectedTrade = useMemo(
    () => (trades || []).find((t) => String(t._id) === selectedTradeId) || null,
    [trades, selectedTradeId],
  );

  const loadCommissionActivity = useCallback(async (tradeId) => {
    if (!tradeId) {
      setRealEstateTrustEFTs([]);
      setCommissionTrustEFTs([]);
      setEftReversals([]);
      return;
    }
    setLoadingActivity(true);
    try {
      const [retRes, ctRes, revRes] = await Promise.all([
        axiosInstance.get(`/real-estate-trust-eft/trade/${tradeId}`),
        axiosInstance.get(`/commission-trust-eft/trade/${tradeId}`),
        axiosInstance.get("/eft-reversals", { params: { tradeId } }),
      ]);
      setRealEstateTrustEFTs(retRes.data || []);
      setCommissionTrustEFTs(ctRes.data || []);
      setEftReversals(revRes.data || []);
    } catch (e) {
      console.error(e);
      setRealEstateTrustEFTs([]);
      setCommissionTrustEFTs([]);
      setEftReversals([]);
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTradeId("");
      setRealEstateTrustEFTs([]);
      setCommissionTrustEFTs([]);
      setEftReversals([]);
      return;
    }
    loadCommissionActivity(selectedTradeId);
  }, [isOpen, selectedTradeId, loadCommissionActivity]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const commissionActivityRows = useMemo(
    () => filterCommissionTrustActivityEFTs(commissionTrustEFTs),
    [commissionTrustEFTs],
  );

  if (!isOpen) return null;

  const tradeNumber =
    selectedTrade?.tradeNumber ?? selectedTrade?.keyInfo?.tradeNumber ?? "";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-details-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-[min(98vw,1200px)] max-h-[min(92vh,900px)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2
              id="commission-details-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Commission Details
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Select a trade to view Real Estate Trust payments, Commission Trust
              activity, and EFT reversals for that trade.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="px-5 py-4 border-b border-gray-100 shrink-0">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Trade
          </label>
          <Select
            inputId="commission-details-trade-select"
            options={tradeOptions}
            value={selectedTradeOption}
            onChange={(opt) => setSelectedTradeId(opt?.value || "")}
            isClearable
            isSearchable
            isDisabled={loadingTrades}
            placeholder="Search or select a trade…"
            noOptionsMessage={() => "No matching trades"}
            styles={tradeSelectStyles}
            className="text-sm"
            classNamePrefix="commission-details-trade"
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
          />
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 min-h-0">
          {loadingTrades ? (
            <p className="text-sm text-gray-500 py-12 text-center">Loading trades…</p>
          ) : !selectedTradeId ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Select a trade to view commission activity.
            </p>
          ) : loadingActivity ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Loading commission activity…
            </p>
          ) : (
            <>
              {tradeNumber && (
                <p className="text-sm font-medium text-gray-800 mb-4">
                  Trade #{tradeNumber}
                </p>
              )}
              <EftTable
                title="Real Estate Trust Payments"
                rows={realEstateTrustEFTs}
                emptyMessage="No Real Estate Trust payments for this trade."
              />
              <EftTable
                title="Commission Trust Activity"
                rows={commissionActivityRows}
                emptyMessage="No Commission Trust activity for this trade."
              />
              <ReversalsTable rows={eftReversals} />
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 shrink-0 flex justify-end bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommissionDetailsModal;
